import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { C } from '../lib/tokens'
import { Card, Pill, Btn } from './UI'

const LOADING_QUOTES = [
  'Tuning up your health command center.',
  'Pulling your portal into focus.',
  'Good data takes a tiny victory lap.',
  'Checking the chart, polishing the buttons.',
  'Almost there. The portal is stretching first.',
  'Lining up labs, orders, and next steps.',
]

function SkeletonStyles() {
  return (
    <style>{`
      @keyframes portal-skeleton-shimmer {
        0% { background-position: 120% 0; }
        100% { background-position: -120% 0; }
      }
    `}</style>
  )
}

function PortalLoadingStyles() {
  return (
    <style>{`
      @keyframes portal-loading-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes portal-loading-sweep {
        0% { transform: translateX(-100%); opacity: 0; }
        18% { opacity: 1; }
        82% { opacity: 1; }
        100% { transform: translateX(240%); opacity: 0; }
      }
      @keyframes portal-loading-rise {
        0%, 100% { transform: scaleY(0.45); opacity: 0.55; }
        50% { transform: scaleY(1); opacity: 1; }
      }
      @keyframes portal-loading-fade {
        0% { opacity: 0; transform: translateY(3px); }
        18%, 82% { opacity: 1; transform: translateY(0); }
        100% { opacity: 0; transform: translateY(-3px); }
      }
      @media (prefers-reduced-motion: reduce) {
        .portal-loading-spin,
        .portal-loading-sweep,
        .portal-loading-rise,
        .portal-loading-fade {
          animation: none !important;
        }
      }
    `}</style>
  )
}

export function PortalLoadingScreen({ label = 'Preparing your portal...' }) {
  const [quoteIndex, setQuoteIndex] = useState(() => Math.floor(Date.now() / 3800) % LOADING_QUOTES.length)

  useEffect(() => {
    const timer = window.setInterval(() => {
      setQuoteIndex(current => (current + 1) % LOADING_QUOTES.length)
    }, 3800)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 28,
        background: 'linear-gradient(145deg, #F4FAF8 0%, #EDF5FF 46%, #F8FAFD 100%)',
        color: C.text,
      }}
    >
      <PortalLoadingStyles />
      <div
        style={{
          width: 'min(420px, 100%)',
          display: 'grid',
          justifyItems: 'center',
          gap: 18,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 142,
            height: 142,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <div
            className="portal-loading-spin"
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 999,
              border: `2px solid ${C.border}`,
              borderTopColor: C.green,
              borderRightColor: C.blue,
              animation: 'portal-loading-spin 1.35s linear infinite',
            }}
          />
          <div
            style={{
              width: 104,
              height: 104,
              borderRadius: 28,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(255,255,255,0.92)',
              border: `1px solid ${C.border}`,
              boxShadow: C.shadowMedium,
              overflow: 'hidden',
            }}
          >
            <img
              src="/logo.png"
              alt="KIS Rx"
              style={{ width: 76, height: 76, borderRadius: 22, objectFit: 'cover' }}
            />
          </div>
        </div>

        <div>
          <div style={{ fontSize: 22, fontWeight: 820, letterSpacing: 0, lineHeight: 1.1 }}>
            {label}
          </div>
          <div
            key={quoteIndex}
            className="portal-loading-fade"
            style={{
              marginTop: 9,
              minHeight: 24,
              fontSize: 14.5,
              lineHeight: 1.45,
              color: C.textSecondary,
              animation: 'portal-loading-fade 3.8s ease-in-out infinite',
            }}
          >
            {LOADING_QUOTES[quoteIndex]}
          </div>
        </div>

        <div
          style={{
            width: 'min(260px, 80vw)',
            height: 8,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.72)',
            border: `1px solid ${C.border}`,
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 2px rgba(14,20,30,0.06)',
          }}
        >
          <div
            className="portal-loading-sweep"
            style={{
              width: '44%',
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${C.green} 0%, ${C.blue} 58%, ${C.purple} 100%)`,
              animation: 'portal-loading-sweep 1.65s ease-in-out infinite',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 5, alignItems: 'end', height: 22 }}>
          {[0, 1, 2, 3].map(index => (
            <span
              key={index}
              className="portal-loading-rise"
              style={{
                width: 5,
                height: 18,
                borderRadius: 999,
                background: [C.green, C.blue, C.purple, C.orange][index],
                transformOrigin: 'bottom',
                animation: `portal-loading-rise 0.95s ease-in-out ${index * 0.12}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function SkeletonBlock({ width = '100%', height = 12, radius = 999, style }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        width,
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, #ECEFF4 0%, #FAFBFD 45%, #ECEFF4 90%)',
        backgroundSize: '220% 100%',
        animation: 'portal-skeleton-shimmer 1.35s ease-in-out infinite',
        ...style,
      }}
    />
  )
}

function SkeletonCircle({ size = 38 }) {
  return <SkeletonBlock width={size} height={size} radius={size / 2} />
}

function SkeletonIntro() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 18, alignItems: 'end', marginBottom: 22 }}>
      <div style={{ display: 'grid', gap: 10 }}>
        <SkeletonBlock width={92} height={10} />
        <SkeletonBlock width="min(360px, 72%)" height={34} radius={12} />
        <SkeletonBlock width="min(640px, 92%)" height={13} />
        <SkeletonBlock width="min(520px, 78%)" height={13} />
      </div>
      <SkeletonBlock width={128} height={38} radius={19} style={{ justifySelf: 'end' }} />
    </div>
  )
}

function SkeletonCard() {
  return (
    <Card style={{ padding: 18, minHeight: 138, borderRadius: 20, boxShadow: C.shadowSoft }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <SkeletonCircle size={38} />
        <SkeletonBlock width={58} height={22} radius={11} />
      </div>
      <div style={{ display: 'grid', gap: 9, marginTop: 32 }}>
        <SkeletonBlock width="74%" height={14} />
        <SkeletonBlock width="94%" height={11} />
        <SkeletonBlock width="62%" height={11} />
      </div>
    </Card>
  )
}

export function SkeletonRows({ count = 4, withIcon = true }) {
  return (
    <>
      <SkeletonStyles />
      <div style={{ display: 'grid', gap: 10 }}>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: withIcon ? '34px minmax(0, 1fr) 76px' : 'minmax(0, 1fr) 76px',
              gap: 12,
              alignItems: 'center',
              padding: '13px 0',
              borderTop: index ? `1px solid ${C.border}` : 0,
            }}
          >
            {withIcon && <SkeletonCircle size={34} />}
            <div style={{ display: 'grid', gap: 8 }}>
              <SkeletonBlock width={index % 2 ? '62%' : '76%'} height={13} />
              <SkeletonBlock width={index % 2 ? '86%' : '68%'} height={10} />
            </div>
            <SkeletonBlock width={76} height={24} radius={12} />
          </div>
        ))}
      </div>
    </>
  )
}

function SkeletonGrid({ count = 4 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
      {Array.from({ length: count }).map((_, index) => <SkeletonCard key={index} />)}
    </div>
  )
}

function SkeletonTable() {
  return (
    <Card style={{ padding: 0, overflow: 'hidden', boxShadow: C.shadowSoft }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'grid', gap: 8 }}>
        <SkeletonBlock width={180} height={15} />
        <SkeletonBlock width={260} height={11} />
      </div>
      <div style={{ padding: '4px 20px 8px' }}>
        <SkeletonRows count={5} />
      </div>
    </Card>
  )
}

function SkeletonMessages() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(210px, 0.42fr) minmax(0, 1fr)', gap: 16 }}>
      <Card style={{ padding: 14, boxShadow: C.shadowSoft }}>
        <SkeletonRows count={5} />
      </Card>
      <Card style={{ padding: 18, minHeight: 360, boxShadow: C.shadowSoft }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <SkeletonCircle size={40} />
          <div style={{ display: 'grid', gap: 8, flex: 1 }}>
            <SkeletonBlock width={180} height={14} />
            <SkeletonBlock width={260} height={10} />
          </div>
        </div>
        <div style={{ display: 'grid', gap: 14, paddingTop: 18 }}>
          <SkeletonBlock width="64%" height={52} radius={16} />
          <SkeletonBlock width="58%" height={48} radius={16} style={{ justifySelf: 'end' }} />
          <SkeletonBlock width="72%" height={64} radius={16} />
        </div>
      </Card>
    </div>
  )
}

function SkeletonShop() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} style={{ padding: 0, minHeight: 280, overflow: 'hidden', boxShadow: C.shadowSoft }}>
            <SkeletonBlock width="100%" height={152} radius={0} />
            <div style={{ padding: 18, display: 'grid', gap: 10 }}>
              <SkeletonBlock width="70%" height={20} radius={10} />
              <SkeletonBlock width="94%" height={11} />
              <SkeletonBlock width="76%" height={11} />
              <SkeletonBlock width={112} height={32} radius={16} />
            </div>
          </Card>
        ))}
      </div>
      <SkeletonRows count={3} />
    </div>
  )
}

function inferSkeletonKind(label) {
  const value = label.toLowerCase()
  if (value.includes('message')) return 'messages'
  if (value.includes('shop')) return 'shop'
  if (value.includes('dashboard') || value.includes('account')) return 'grid'
  if (value.includes('order') || value.includes('prescription') || value.includes('form') || value.includes('lab') || value.includes('visit')) return 'table'
  return 'rows'
}

export function PortalSkeleton({ label = 'Loading...', kind }) {
  const skeletonKind = kind || inferSkeletonKind(label)
  return (
    <div role="status" aria-label={label} aria-busy="true">
      <SkeletonStyles />
      <SkeletonIntro />
      {skeletonKind === 'messages' ? <SkeletonMessages /> : null}
      {skeletonKind === 'shop' ? <SkeletonShop /> : null}
      {skeletonKind === 'grid' ? <SkeletonGrid count={4} /> : null}
      {skeletonKind === 'table' ? <SkeletonTable /> : null}
      {skeletonKind === 'rows' ? (
        <Card style={{ padding: 22, boxShadow: C.shadowSoft }}>
          <SkeletonRows count={4} />
        </Card>
      ) : null}
      <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
        {label}
      </span>
    </div>
  )
}

export function PageIntro({ eyebrow, title, copy, action, meta }) {
  return (
    <div className="portal-page-intro" style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: 18,
      alignItems: 'end',
      marginBottom: 22,
    }}>
      <div>
        {eyebrow && (
          <div style={{
            fontSize: 12,
            fontWeight: 780,
            color: C.textTertiary,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            {eyebrow}
          </div>
        )}
        <h1 className="portal-page-title" style={{
          margin: eyebrow ? '5px 0 0' : 0,
          fontSize: 36,
          lineHeight: 1.05,
          fontWeight: 790,
          color: C.text,
          letterSpacing: 0,
        }}>
          {title}
        </h1>
        {copy && (
          <div style={{
            marginTop: 9,
            maxWidth: 720,
            fontSize: 14.5,
            lineHeight: 1.5,
            color: C.textSecondary,
          }}>
            {copy}
          </div>
        )}
      </div>
      {(action || meta) && (
        <div className="portal-page-intro-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {meta}
          {action}
        </div>
      )}
    </div>
  )
}

export function NoticeCard({ tone = 'yellow', children }) {
  const colors = {
    yellow: { bg: '#FFF9E6', border: '#F2D27B', text: '#7A5A0B' },
    red: { bg: '#FFF2F2', border: '#FFB8B3', text: '#A6201B' },
    green: { bg: '#F0FFF7', border: '#B8E8CD', text: '#0F7A3E' },
    blue: { bg: '#F3F8FF', border: '#C8E0FF', text: '#0057B8' },
  }[tone] || { bg: '#FFF9E6', border: '#F2D27B', text: '#7A5A0B' }

  return (
    <Card style={{ padding: 16, background: colors.bg, borderColor: colors.border, boxShadow: C.shadowSoft }}>
      <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.45 }}>{children}</div>
    </Card>
  )
}

export function EmptyState({ icon: Icon, title, copy, actions }) {
  return (
    <div style={{
      padding: 24,
      border: `1px solid ${C.border}`,
      borderRadius: 18,
      background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
      display: 'grid',
      gap: 12,
      justifyItems: 'start',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.82)',
    }}>
      {Icon && (
        <span style={{
          width: 42,
          height: 42,
          borderRadius: 21,
          background: '#fff',
          border: `1px solid ${C.border}`,
          color: C.text,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={19} strokeWidth={2} />
        </span>
      )}
      <div>
        <div style={{ fontSize: 15, fontWeight: 760, color: C.text }}>{title}</div>
        {copy && (
          <div style={{ fontSize: 13.5, color: C.textSecondary, lineHeight: 1.45, marginTop: 6 }}>
            {copy}
          </div>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  )
}

export function ActionCard({ to, icon: Icon, title, copy, pill, onClick, compact = false }) {
  if (compact) {
    const compactContent = (
      <Card
        className="portal-action-card portal-action-card-compact"
        onClick={onClick}
        style={{
          padding: 14,
          minHeight: 82,
          borderRadius: 18,
          display: 'grid',
          gridTemplateColumns: '34px minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 12,
          boxShadow: C.shadowSoft,
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFE 100%)',
        }}
      >
        <span className="portal-action-icon" style={{
          width: 34,
          height: 34,
          borderRadius: 17,
          background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F4F8 100%)',
          color: C.text,
          border: `1px solid ${C.hairline}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {Icon && <Icon size={17} strokeWidth={2} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 780, color: C.text, letterSpacing: 0 }}>{title}</div>
          <div style={{ fontSize: 12.2, color: C.textSecondary, lineHeight: 1.35, marginTop: 3, overflowWrap: 'anywhere' }}>{copy}</div>
        </div>
        {pill ? <Pill color={pill.color}>{pill.label}</Pill> : <ChevronRight size={16} color={C.textTertiary} />}
      </Card>
    )

    if (!to) return compactContent
    return <Link to={to} style={{ textDecoration: 'none' }}>{compactContent}</Link>
  }

  const content = (
    <Card
      className="portal-action-card"
      onClick={onClick}
      style={{
        padding: 18,
        minHeight: 138,
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 18,
        boxShadow: C.shadowSoft,
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFE 100%)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <span className="portal-action-icon" style={{
          width: 38,
          height: 38,
          borderRadius: 19,
          background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F4F8 100%)',
          color: C.text,
          border: `1px solid ${C.hairline}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {Icon && <Icon size={18} strokeWidth={2} />}
        </span>
        {pill ? <Pill color={pill.color}>{pill.label}</Pill> : <ChevronRight size={17} color={C.textTertiary} />}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 760, color: C.text, letterSpacing: 0 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.4, marginTop: 5 }}>{copy}</div>
      </div>
    </Card>
  )

  if (!to) return content
  return <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link>
}

export function ListShell({ title, caption, children, action }) {
  return (
    <Card className="portal-list-shell" style={{ padding: 0, overflow: 'hidden', boxShadow: C.shadowMedium }}>
      <div className="portal-list-shell-header" style={{
        padding: '18px 22px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.86) 100%)',
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 780, color: C.text }}>{title}</div>
          {caption && <div style={{ fontSize: 12.5, color: C.textSecondary, marginTop: 4 }}>{caption}</div>}
        </div>
        {action}
      </div>
      {children}
    </Card>
  )
}

export function RowLink({ to, title, subtitle, right, icon: Icon }) {
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className={`portal-row-link ${Icon ? 'portal-row-link-has-icon' : 'portal-row-link-no-icon'}`} style={{
        display: 'grid',
        gridTemplateColumns: Icon ? '34px minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'center',
        padding: '16px 22px',
        borderBottom: `1px solid ${C.border}`,
        background: '#FFFFFF',
      }}>
        {Icon && (
          <span style={{
            width: 34,
            height: 34,
            borderRadius: 17,
            background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F4F8 100%)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.text,
          }}>
            <Icon size={17} strokeWidth={2} />
          </span>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 14.5,
            fontWeight: 720,
            color: C.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {title}
          </div>
          {subtitle && <div style={{ fontSize: 12.5, color: C.textSecondary, marginTop: 4, lineHeight: 1.35 }}>{subtitle}</div>}
        </div>
        {right && <div className="portal-row-link-right">{right}</div>}
      </div>
    </Link>
  )
}

export function LoadingCard({ label = 'Loading...' }) {
  return <PortalSkeleton label={label} />
}

export function PrimaryLinkButton({ to, children }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <Btn variant="green">{children}</Btn>
    </Link>
  )
}
